import { OffsetUnit } from "@sp-crm/core";
import { GetWorkflowSettingsResult } from "generated/graphql";
import React, { useMemo } from "react";
import { pluralize } from "util/pluralize";
import {
    computeWorkflowDelay,
    hasEditableDelay,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowDelaySummaryProps {
    workflow: EditableWorkflow;
    workflowSettings?: GetWorkflowSettingsResult;
}

const fieldPatterns: Record<string, string> = {
    "{{client.moveTimelineActual}}": "the move date",
    "{{client.statusCheckLaterDate}}": "the check back later date",
};

const unitLabels: Record<string, string> = {
    d: "day",
    h: "hour",
    m: "minute",
};

const describeDelayRecipe = (
    delayRecipe: string | null,
    workflow: EditableWorkflow,
    workflowSettings?: GetWorkflowSettingsResult,
): string | null => {
    if (!delayRecipe) return null;

    for (const [token, label] of Object.entries(fieldPatterns)) {
        if (delayRecipe.includes(token)) {
            const fixedOffsetMatch = delayRecipe.match(/\+(\d+)([dhm])$/);
            if (fixedOffsetMatch) {
                const [, num, unit] = fixedOffsetMatch;
                const numValue = parseInt(num, 10);
                const unitName = unitLabels[unit] || unit;
                return `${num} ${pluralize(numValue, unitName)} after ${label}`;
            }

            const settingsMatch = delayRecipe.match(/\+\{\{settings\.(\w+)\}\}([dhm])$/);
            if (settingsMatch && workflowSettings) {
                const [, settingKey, unit] = settingsMatch;
                const resolvedValue = resolveWorkflowSetting(
                    workflowSettings,
                    workflow,
                    settingKey,
                );

                if (resolvedValue) {
                    const numValue = parseInt(resolvedValue, 10);
                    const unitName = unitLabels[unit] || unit;
                    return `${resolvedValue} ${pluralize(numValue, unitName)} after ${label}`;
                }

                return `After ${label} (configurable delay)`;
            }
        }
    }

    return null;
};

const formatDelay = (delay: number, unit: OffsetUnit): string => {
    if (delay === 0) {
        return "Immediately";
    }

    const unitName = unitLabels[unit] || unit;
    return `${delay} ${pluralize(delay, unitName)} after trigger`;
};

export const WorkflowDelaySummaryListItem: React.FC<WorkflowDelaySummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const delayDescription = useMemo(() => {
        if (!hasEditableDelay(workflow)) {
            return null;
        }

        const recipeDescription = describeDelayRecipe(
            workflow.delayRecipe,
            workflow,
            workflowSettings,
        );
        if (recipeDescription) {
            return recipeDescription;
        }

        const [delay, unit] = computeWorkflowDelay(workflow);
        return formatDelay(delay, unit);
    }, [workflow, workflowSettings]);

    if (!delayDescription) {
        return null;
    }

    return (
        <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
                <span className="font-medium">Timing:</span> {delayDescription}
            </span>
        </li>
    );
};
